import { useCallback, useRef } from "react";
import { EmployeeAttendanceLocationType } from "features/EmployeeAttendance/constants/employee-attendance.constants";
import { Col, Container, Row } from "reactstrap";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { OnLeaveTodayCard } from "./OnLeaveTodayCard";
import { WfhTodayCard } from "./WfhTodayCard";
import { ActiveEmployeesCard } from "./ActiveEmployeesCard";
import AttendanceButton from "./AttendanceButton";
import WorkAssignmentsDashboardCard from "./WorkAssignmentsDashboardCard";
import PendingLeavesDashboardCard from "./PendingLeavesDashboardCard";
import MaterialsOutDashboardCard from "./MaterialsOutDashboardCard";
import ToolsListDashboardCard from "./ToolsListDashboardCard";
import { Widgets } from "./Widgets";
import { useDashboardSummary } from "./hooks/useDashboardSummary";

const Dashboard = () => {
  document.title = getDocumentTitle("Dashboard");

  const { hasEmployeeRecord, user } = useProfile();
  const { can } = useAccess();

  const employeeId = user?.employee?.id;

  const canApproveLeave = can(Permissions.LEAVE_APPLICATION.APPROVE);
  const canRejectLeave = can(Permissions.LEAVE_APPLICATION.REJECT);
  const canReadWorkAssignments = can(Permissions.WORK_ASSIGNMENT.READ);
  const canReadAllEmployees = can(Permissions.EMPLOYEE.READ);
  const canReadMaterialInOut = can(Permissions.MATERIAL_IN_OUT.READ);
  /** Spec calls this widget visible to all users — should be seeded broadly by the backend RBAC catalog. */
  const canReadToolInOut = can(Permissions.TOOL_IN_OUT.READ);

  const showPendingLeaves = canApproveLeave || canRejectLeave;

  const { summary, loading, error } = useDashboardSummary();

  const activeEmployeesRef = useRef<(() => void) | null>(null);
  const wfhRef = useRef<(() => void) | null>(null);

  const handleAttendanceSuccess = useCallback((inLocationType?: EmployeeAttendanceLocationType) => {
    activeEmployeesRef.current?.();
    if (inLocationType === EmployeeAttendanceLocationType.WORK_FROM_HOME) {
      wfhRef.current?.();
    }
  }, []);

  const hasMainContent =
    showPendingLeaves ||
    canReadWorkAssignments ||
    canReadMaterialInOut ||
    canReadToolInOut;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Dashboard" pageTitle="Home" />

        {!loading && error ? (
          <p className="text-warning small mb-3">{error}</p>
        ) : null}

        {hasEmployeeRecord ? (
          <AttendanceButton variant="banner" employeeId={employeeId} onSuccess={handleAttendanceSuccess} />
        ) : null}

        <Widgets summary={summary} loading={loading} error={error} />

        <Row>
          {hasMainContent ? (
            <Col xl={8}>
              {showPendingLeaves ? <PendingLeavesDashboardCard /> : null}
              {canReadWorkAssignments ? <WorkAssignmentsDashboardCard /> : null}
              {canReadMaterialInOut ? <MaterialsOutDashboardCard /> : null}
              {canReadToolInOut ? <ToolsListDashboardCard /> : null}
            </Col>
          ) : null}

          <Col xl={hasMainContent ? 4 : 12}>
            {canReadAllEmployees ? (
              <ActiveEmployeesCard refreshRef={activeEmployeesRef} />
            ) : null}
            <OnLeaveTodayCard />
            <WfhTodayCard refreshRef={wfhRef} />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Dashboard;
